import React, { useState } from 'react';
import * as Yup from 'yup';
import { Alert, Box, Collapse, Container, Step, StepLabel, Stepper } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { clamp, flatten, get, includes, isEmpty, isNil, map, noop, pick, some } from 'lodash';
import type { FieldErrors, Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import RestaurantForm from './restaurant_form';
import ContactForm from './contact_form';
import JobDetailForm from './job_detail_form';
import PaymentForm from './payment_form';
import { createScopedI18n } from '../../i18n/i18n';
import Stash, { CREATE_FAST_TRACK } from '../../utils/stash';
import LogoImage from '../../assets/logo.png';
import { apiAxios, InAppBrowserBanner } from '../../components';
import { joinPairs } from '../../utils/libs';

const MAX_SCHEDULE_DAYS = 12;
const MIN_START_AT_HOURS = 3;
const MIN_JOB_DURATION_HOURS = 5;
const rePhoneNumber = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
export const DEFAULT_FAST_TRACK_STASH_KEY = 'FAST_TRACK_FORM';

const restaurantErrorI18n = createScopedI18n('graphql.errors.types.restaurant.fields', { joinOutput: true });
const registerErrorI18n = createScopedI18n('graphql.errors.types.register.fields', { joinOutput: true });
const jobErrorI18n = createScopedI18n('graphql.errors.types.job.fields', { joinOutput: true });
const fastTrackI18n = createScopedI18n('pages.fast_track', { joinOutput: true });

const steps = [
  fastTrackI18n('steps.step_1'),
  fastTrackI18n('steps.step_2'),
  fastTrackI18n('steps.step_3'),
  fastTrackI18n('steps.step_4'),
];

const CreateGuestJobSchema = Yup.object({
  // restaurant
  restaurantName: Yup.string()
    .required(restaurantErrorI18n('name.blank'))
    .matches(/^(?!.*[[\]*/\\?:"|]).+$/, restaurantErrorI18n('name.invalid')),
  branchName: Yup.string()
    .required(restaurantErrorI18n('branchName.blank'))
    .matches(/^(?!.*[[\]*/\\?:"|]).+$/, restaurantErrorI18n('branchName.invalid')),
  landmark: Yup.string().required(restaurantErrorI18n('landmark.blank')),
  location: Yup.object({
    latitude: Yup.number().required(restaurantErrorI18n('location.blank')),
    longitude: Yup.number().required(restaurantErrorI18n('location.blank')),
  }).required(restaurantErrorI18n('location.blank')),
  placeId: Yup.string(),
  photoFrontToken: Yup.string().required(restaurantErrorI18n('photo.blank')),
  photoAdd1Token: Yup.string().required(restaurantErrorI18n('photo.blank')),
  photoAdd2Token: Yup.string().required(restaurantErrorI18n('photo.blank')),

  // job
  scheduleDates: Yup.array()
    .of(
      Yup.lazy(() =>
        Yup.date()
          .min(dayjs().startOf('day'), jobErrorI18n('schedule_date.too_short'))
          .max(
            dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days'),
            jobErrorI18n('schedule_date.too_long', { count: MAX_SCHEDULE_DAYS }),
          )
          .required(jobErrorI18n('schedule_date.blank')),
      ),
    )
    .required(jobErrorI18n('schedule_dates.blank'))
    .min(1, jobErrorI18n('schedule_dates.too_short', { count: 1 })),
  startTime: Yup.date()
    .typeError(fastTrackI18n('validation.startTime.invalid'))
    .required(fastTrackI18n('validation.startTime.blank'))
    .test({
      // validate that start time is a multiple of 30 minutes
      message: fastTrackI18n('validation.startTime.invalid'),
      test: (startTime) => startTime.getMinutes() % 30 === 0,
    })
    .test({
      // validate that start time is not in the past
      message: fastTrackI18n('validation.startTime.not_in_past'),
      test: (startTime, context) => {
        try {
          const { scheduleDates } = context.parent;
          const firstDate = scheduleDates[0];
          firstDate.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
          return dayjs().isSameOrBefore(firstDate);
        } catch (error) {
          return true; // if context.parent.scheduleDates is not available, skip validation
        }
      },
    })
    .test({
      // validate that start time is at least 3 hours after now
      message: fastTrackI18n('validation.startTime.at_least_3_hours_after_now'),
      test: (startTime, context) => {
        try {
          const { scheduleDates } = context.parent;
          const firstDate = scheduleDates[0];
          firstDate.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
          const diffHours = dayjs(firstDate).diff(dayjs(), 'hours', true);
          return diffHours >= 3; // ensure start time is not in the past
        } catch (error) {
          return true; // if context.parent.scheduleDates is not available, skip validation
        }
      },
    })
    .test({
      // validate that start time is at least 5 hours before end time
      message: fastTrackI18n('validation.startTime.at_least_5_hours_before_end_time'),
      test: (startTime, context) => {
        try {
          const { endTime } = context.parent;
          if (endTime.getTime() <= startTime.getTime()) endTime.setDate(startTime.getDate() + 1); // if end time is before start time, assume it's the next day
          const diffHours = dayjs(endTime).diff(startTime, 'hours', true);
          return diffHours >= 5;
        } catch (error) {
          return true; // if context.parent.endTime is not available, skip validation
        }
      },
    }),
  endTime: Yup.date()
    .typeError(jobErrorI18n('job_end_at.invalid'))
    .required(jobErrorI18n('job_end_at.blank'))
    .test({
      message: jobErrorI18n('job_end_at.invalid'),
      test: (endTime) => endTime.getMinutes() % 30 === 0,
    })
    .test({
      message: jobErrorI18n('job_end_at.invalid'),
      test: (endTime, context) => {
        try {
          const { startTime } = context.parent;
          if (endTime.getTime() <= startTime.getTime()) endTime.setDate(startTime.getDate() + 1); // if end time is before start time, assume it's the next day
          const diffHours = dayjs(endTime).diff(startTime, 'hours', true);
          return diffHours >= MIN_JOB_DURATION_HOURS;
        } catch (error) {
          return true; // if context.parent.startTime is not available, skip validation
        }
      },
    }),
  scheduleDateTimes: Yup.array().of(
    Yup.object({
      jobStartAt: Yup.lazy(() =>
        Yup.date()
          .required()
          .min(
            dayjs().add(MIN_START_AT_HOURS, 'hours'),
            jobErrorI18n('job_start_at.too_short', { count: MIN_START_AT_HOURS }),
          ),
      ),
      jobEndAt: Yup.date()
        .required()
        .when('jobStartAt', ([jobStartAt], schema) => {
          if (jobStartAt) {
            return schema.min(
              dayjs(jobStartAt).add(MIN_JOB_DURATION_HOURS, 'hours'),
              jobErrorI18n('job_end_at.invalid'),
            );
          }
          return schema;
        }),
    }),
  ),
  gender: Yup.string()
    .required(jobErrorI18n('gender.blank'))
    .oneOf(['male', 'female', 'SET_NULL_ON_SUBMIT'], jobErrorI18n('gender.invalid')),
  breakDurationMinute: Yup.number()
    .required(jobErrorI18n('break_duration.blank'))
    .min(0, jobErrorI18n('break_duration.too_short', { count: 0 })),
  numberOfPosition: Yup.number()
    .required(jobErrorI18n('number_of_position.blank'))
    .min(1, jobErrorI18n('number_of_position.too_short', { count: 1 })),
  jobType: Yup.string().required(jobErrorI18n('job_type.blank')),
  duties: Yup.array()
    .of(Yup.string().required(jobErrorI18n('duty.blank')))
    .required(jobErrorI18n('duty.blank'))
    .when('jobType', ([jobType], schema) => {
      if (isNil(jobType) || jobType === 'other') {
        return schema;
      }
      return schema.min(1, jobErrorI18n('duty.blank'));
    }),
  remark: Yup.string()
    .nullable()
    .when('jobType', ([jobType], schema) =>
      isNil(jobType) || jobType === 'other' ? schema.required(jobErrorI18n('remark.blank')) : schema,
    ),

  // contact
  contactEmail: Yup.string()
    .required(registerErrorI18n('contact_email.blank'))
    .email(registerErrorI18n('contact_email.invalid')),
  contactPhoneNumber: Yup.string()
    .required(registerErrorI18n('contact_phone_number.blank'))
    .matches(rePhoneNumber, registerErrorI18n('contact_phone_number.invalid')),

  // payment
  referenceId: Yup.string(),
});
export type CreateGuestJobValueType = Yup.InferType<typeof CreateGuestJobSchema> & {
  token?: string;
  otpFormHtml?: string;
  jobRefs?: string[];
  restoreStep?: number;
  status?: 'pending' | 'complete' | 'error';
  discountCode?: string | null;
};

function GuestJobPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const stash = Stash(CREATE_FAST_TRACK);
  const form = useForm<CreateGuestJobValueType>({
    resolver: yupResolver(CreateGuestJobSchema) as Resolver<CreateGuestJobValueType>,
    defaultValues: {
      scheduleDates: [],
      numberOfPosition: 1,
      breakDurationMinute: 0,
      duties: [],
      jobType: 'other',
      restoreStep: 0,
    },
  });
  const { handleSubmit, getValues, getFieldState } = form;

  const saveToStash = (values = getValues(), stashKey: string = DEFAULT_FAST_TRACK_STASH_KEY) => {
    const data = pick(values, [
      'restaurantName',
      'branchName',
      'landmark',
      'location',
      'placeId',
      'photoFrontToken',
      'photoAdd1Token',
      'photoAdd2Token',

      'scheduleDates',
      'startTime',
      'endTime',
      'scheduleDateTimes',
      'gender',
      'breakDurationMinute',
      'numberOfPosition',
      'jobType',
      'duties',
      'remark',

      'contactEmail',
      'contactPhoneNumber',

      'status',
      'otpFormHtml',
      'jobRefs',
      'restoreStep',
    ]);
    stash.save(stashKey, data);
  };

  const updateStash = (values: CreateGuestJobValueType, referenceId?: string, restoreStep?: number) => {
    let nextRestoreStep = restoreStep;
    if (isNil(nextRestoreStep)) {
      nextRestoreStep = (step ?? 0) + 1;
    }
    nextRestoreStep = clamp(nextRestoreStep, 0, steps.length - 1);
    const nextValues = { ...values, restoreStep: nextRestoreStep };

    saveToStash(nextValues, DEFAULT_FAST_TRACK_STASH_KEY);
    if (referenceId) {
      saveToStash(nextValues, referenceId);
    }
  };

  React.useEffect(() => {
    const values = stash.load(DEFAULT_FAST_TRACK_STASH_KEY);
    if (values) {
      const restoreStep = get(values, 'restoreStep', 0);

      if (values.startTime) values.startTime = dayjs(values.startTime);
      if (values.endTime) values.endTime = dayjs(values.endTime);

      form.reset(values, { keepDefaultValues: true });
      setStep(restoreStep);
    }
  }, []);

  const submitHandler = async (value: CreateGuestJobValueType) => {
    setSubmitting(true);
    updateStash(value);
    setErrorMessage(null);

    try {
      let scheduleDates = map(get(value, 'scheduleDateTimes'), (dateTime) => ({
        job_start_at: dayjs(dateTime.jobStartAt).second(0).millisecond(0).toISOString(),
        job_end_at: dayjs(dateTime.jobEndAt).second(0).millisecond(0).toISOString(),
      }));

      // Fallback if scheduleDateTimes missing
      if (isEmpty(scheduleDates)) {
        scheduleDates = map(value.scheduleDates, (date) => {
          const startAt = dayjs(value.startTime);
          const endAt = dayjs(value.endTime);

          const jobStartAt = dayjs(date).hour(startAt.hour()).minute(startAt.minute()).second(0).millisecond(0);
          let jobEndAt = dayjs(date).hour(endAt.hour()).minute(endAt.minute()).second(0).millisecond(0);

          if (jobEndAt.isBefore(jobStartAt)) {
            jobEndAt = jobEndAt.add(1, 'days');
          }

          return {
            job_start_at: jobStartAt.toISOString(),
            job_end_at: jobEndAt.toISOString(),
          };
        });
      }

      const response = await apiAxios({
        method: 'post',
        url: '/create_guest_job',
        data: {
          restaurant: {
            name: value.restaurantName,
            branch_name: value.branchName,
            landmark: value.landmark,
            latitude: value.location.latitude,
            longitude: value.location.longitude,
            google_maps_place_id: value.placeId,
            photo_tokens: [
              { token: value.photoFrontToken, photo_type: 'front' },
              { token: value.photoAdd1Token, photo_type: 'additional1' },
              { token: value.photoAdd2Token, photo_type: 'additional2' },
            ],
          },
          job: {
            schedule_dates: scheduleDates,
            break_duration_minute: value.breakDurationMinute,
            number_of_position: value.numberOfPosition,
            gender: value.gender === 'SET_NULL_ON_SUBMIT' ? null : value.gender,
            job_type: value.jobType,
            duties: value.duties,
          },
          contact: {
            email: value.contactEmail,
            phone_number: value.contactPhoneNumber,
          },
        },
      });

      const resp = response.data;

      if (resp.success) {
        const { data } = resp;
        form.setValue('jobRefs', data.job_references);
        updateStash({ ...getValues(), status: 'pending' });
      } else {
        const nextErrorMessage = joinPairs(get(resp, 'errors'));
        throw new Error(nextErrorMessage);
      }
    } catch (error) {
      const errors = get(error, 'response.data.errors', null);
      let nextErrorMessage = joinPairs(errors) || get(error, 'message', null);

      if (includes(flatten(errors), 'photo_tokens')) {
        form.setError('photoFrontToken', { message: restaurantErrorI18n('photo.invalid') });
        form.setError('photoAdd1Token', { message: restaurantErrorI18n('photo.invalid') });
        form.setError('photoAdd2Token', { message: restaurantErrorI18n('photo.invalid') });
        nextErrorMessage = restaurantErrorI18n('photo.invalid');
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        errorHandler();
      }

      updateStash({ ...getValues(), status: 'error' });
      setErrorMessage(nextErrorMessage);
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);
  };

  const completeHandler = ({ referenceId }: { referenceId?: string }) => {
    if (referenceId) {
      updateStash(getValues(), referenceId);
      navigate(`/fast_track/${referenceId}`);
    }
  };

  const errorHandler = (errors?: FieldErrors<CreateGuestJobValueType>) => {
    // eslint-disable-next-line no-console
    console.debug('errors', errors);

    const haveErrors = (fields: (keyof CreateGuestJobValueType)[]) =>
      some(map(fields, (field) => getFieldState(field).invalid));

    if (
      haveErrors([
        'restaurantName',
        'branchName',
        'landmark',
        'location',
        'placeId',
        'photoFrontToken',
        'photoAdd1Token',
        'photoAdd2Token',
      ])
    ) {
      setStep(0); // Restaurant Form
    } else if (
      haveErrors([
        'scheduleDates',
        'startTime',
        'endTime',
        'gender',
        'breakDurationMinute',
        'numberOfPosition',
        'jobType',
        'duties',
      ])
    ) {
      setStep(1); // Job Form
    } else if (haveErrors(['contactEmail', 'contactPhoneNumber'])) {
      setStep(2); // Contact Form
    }
  };

  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const handleManualSubmit = () => {
    setSubmitting(true);
    handleSubmit(submitHandler, errorHandler)().finally(() => {
      setSubmitting(false);
    });
  };

  const handleNextStep = () => {
    updateStash(getValues(), getValues('referenceId') ?? undefined);
    setStep((prevStep) => {
      const nextStep = prevStep + 1;
      if (nextStep === steps.length) {
        handleManualSubmit();
      }
      return nextStep;
    });
  };

  const handlePreviousStep = () => {
    if (step === 0) return;
    setStep(step - 1);
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <InAppBrowserBanner sticky openDialogOnDetect />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingY: '8px',
        }}
      >
        <Box component="img" src={LogoImage} sx={{ objectFit: 'cover' }} />
      </Box>

      <Container maxWidth="sm">
        <Stepper activeStep={step} alternativeLabel sx={{ paddingY: '16px' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Collapse in={!!errorMessage}>
          <Alert severity="error">{errorMessage}</Alert>
        </Collapse>

        <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {step !== steps.length && (
            <form onSubmit={handleSubmit(submitHandler, errorHandler)}>
              <Box sx={{ visibility: step === 0 ? 'visible' : 'hidden', height: step === 0 ? 'auto' : 0 }}>
                <RestaurantForm form={form} loading={submitting} onNext={handleNextStep} />
              </Box>
              <Box sx={{ visibility: step === 1 ? 'visible' : 'hidden', height: step === 1 ? 'auto' : 0 }}>
                <JobDetailForm
                  form={form}
                  loading={submitting}
                  onNext={handleNextStep}
                  onPrevious={handlePreviousStep}
                />
              </Box>
              <Box sx={{ visibility: step === 2 ? 'visible' : 'hidden', height: step === 2 ? 'auto' : 0 }}>
                <ContactForm form={form} loading={submitting} onNext={handleNextStep} onPrevious={handlePreviousStep} />
              </Box>
              <Box sx={{ visibility: step === 3 ? 'visible' : 'hidden', height: step === 3 ? 'auto' : 0 }}>
                <PaymentForm
                  form={form}
                  loading={submitting}
                  onPrevious={handlePreviousStep}
                  onComplete={completeHandler}
                  onError={errorHandler}
                />
              </Box>
            </form>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default GuestJobPage;
